import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { getCheckoutStatus, getEvent } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { track } from "@/lib/analytics";
import { SetPasswordCard } from "@/components/SetPasswordCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, Calendar, Clock, MapPin } from "lucide-react";

const MAX_POLLS = 8;

const PRODUCT_COPY = {
  "founder-workshop": { title: "Your workshop seat is confirmed.", sub: "A seat is held in your name. Watch your inbox for the room details." },
  "operator-dinner": { title: "Your dinner seat is confirmed.", sub: "Ten operators, one table. Details to your inbox." },
  "retreat-deposit": { title: "Your retreat deposit is in.", sub: "Your seat is held. We will confirm the balance and logistics by email." },
  "the-table-membership": { title: "Welcome to The Table.", sub: "Your annual membership is active. The room is open to you." },
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
  } catch (e) {
    return iso;
  }
}

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const productHint = params.get("product");
  const isFree = params.get("free") === "1";
  const eventSlugParam = params.get("event");
  const [phase, setPhase] = useState(isFree ? "success" : "polling");
  const [data, setData] = useState(
    isFree ? { product_type: "event", event_slug: eventSlugParam, free: true } : null
  );
  const [eventInfo, setEventInfo] = useState(null);
  const [error, setError] = useState("");
  const paidFired = useRef(false);

  const poll = useCallback(
    async (attempt = 0) => {
      if (attempt >= MAX_POLLS) {
        setPhase("error");
        setError("We could not confirm the payment in time. If you were charged, check your email or contact ceo@coppervine.io.");
        return;
      }
      try {
        const d = await getCheckoutStatus(sessionId);
        if (d.payment_status === "paid") {
          if (!paidFired.current) {
            paidFired.current = true;
            track("payment", { offer: d.offer_name, value: (d.amount_total || 0) / 100, currency: (d.currency || "usd").toUpperCase(), transaction_id: sessionId, product: d.product_key });
          }
          setData(d);
          setPhase("success");
          return;
        }
        if (d.status === "expired") {
          setPhase("error");
          setError("This checkout session expired. Please start again.");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch (e) {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    if (isFree) {
      track("event_rsvp_confirmed", { event: eventSlugParam, free: true });
      return;
    }
    if (!sessionId) {
      navigate("/");
      return;
    }
    poll(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    const slug = data?.event_slug || eventSlugParam;
    if (slug) getEvent(slug).then(setEventInfo).catch(() => {});
  }, [data, eventSlugParam]);

  const copy = isFree
    ? { title: "You are registered.", sub: "Your seat is held. Watch your inbox for the confirmation and event details." }
    : PRODUCT_COPY[data?.product_key || productHint] || {
        title: "Payment confirmed.",
        sub: "Thank you. A confirmation is on the way to your inbox.",
      };

  return (
    <div className="exec-scope min-h-screen" data-testid="checkout-success-page">
      <Seo title="Confirmation — Copper & Vine" description="Your Copper & Vine confirmation." noindex />
      <div className="exec-container pt-24 md:pt-28 pb-24 max-w-2xl">
        {phase === "polling" && (
          <div data-testid="checkout-success-processing">
            <p className="exec-eyebrow">Confirming</p>
            <h1 className="font-light text-[clamp(26px,3.2vw,38px)] leading-[1.14] mt-4">Confirming your payment</h1>
            <p className="exec-serif text-[17px] text-[color:var(--lead-ink)] mt-4">One moment. Do not close this tab.</p>
            <div className="mt-8 space-y-3">
              <Skeleton className="h-12 w-full bg-[color:var(--rule)]" />
              <Skeleton className="h-12 w-3/4 bg-[color:var(--rule)]" />
            </div>
          </div>
        )}

        {phase === "success" && (
          <div data-testid="checkout-success-state">
            <div className="w-12 h-12 rounded-full border border-[color:var(--copper)] flex items-center justify-center">
              <Check size={22} className="text-[color:var(--copper-deep)]" />
            </div>
            <p className="exec-eyebrow mt-6">{isFree ? "Registration confirmed" : "Payment confirmed"}</p>
            <h1 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">{copy.title}</h1>
            <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">{copy.sub}</p>

            {/* Concrete event details */}
            {eventInfo && (
              <div data-testid="success-event-details" className="mt-8 exec-card p-6">
                <p className="exec-eyebrow">Your event</p>
                <h2 data-testid="success-event-title" className="font-light text-[clamp(22px,2.4vw,28px)] leading-[1.18] mt-2">{eventInfo.title}</h2>
                <div className="mt-4 space-y-2.5">
                  {eventInfo.event_date && (
                    <p className="flex items-center gap-2.5 exec-serif text-[15.5px] text-[color:var(--lead-ink)]">
                      <Calendar size={16} className="text-[color:var(--copper-deep)] shrink-0" /> {fmtDate(eventInfo.event_date)}
                    </p>
                  )}
                  {eventInfo.event_time && (
                    <p className="flex items-center gap-2.5 exec-serif text-[15.5px] text-[color:var(--lead-ink)]">
                      <Clock size={16} className="text-[color:var(--copper-deep)] shrink-0" /> {eventInfo.event_time}
                    </p>
                  )}
                  {(eventInfo.location || eventInfo.format) && (
                    <p className="flex items-center gap-2.5 exec-serif text-[15.5px] text-[color:var(--lead-ink)]">
                      <MapPin size={16} className="text-[color:var(--copper-deep)] shrink-0" /> {eventInfo.location || eventInfo.format}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="mt-8 space-y-4">
              {!isFree && sessionId && <SetPasswordCard email={data?.buyer_email} sessionId={sessionId} />}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/hub" className="exec-btn exec-btn-primary" data-testid="success-hub-link">Go to the Hub</Link>
                {(data?.product_type === "event") && (
                  <Link to="/events" className="exec-btn exec-btn-secondary" data-testid="success-events-link">Back to events</Link>
                )}
              </div>
            </div>
          </div>
        )}

        {phase === "error" && (
          <div data-testid="checkout-success-error" className="border-l-2 border-[color:var(--copper)] pl-5 py-2">
            <p className="exec-eyebrow">Something went wrong</p>
            <p className="exec-serif text-[17px] text-[color:var(--lead-ink)] mt-3">{error}</p>
            <Link to="/" className="exec-btn exec-btn-secondary mt-6">Back home</Link>
          </div>
        )}
      </div>
    </div>
  );
}
